import Image from '@/components/Image';
import VizyBlogArticle from '@/components/vizy/VizyBlogArticleResourceDownload';
import { CraftWebinarType } from '@/constants/craft';
import { RESOURCE_DOWNLOAD_BYPASS_KEY } from '@/constants/session-storage';
import { ResourceQuery } from '@/graphql';
import gtm from '@/lib/gtm';
import useBypassCookie from '@/lib/hooks/useBypassCookie';
import { getSingle, maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import ResourceCardsModule, { resourceCardsModuleHasCards } from '@/modules/ResourceCards';
import MasterTemplate from '@/templates/MasterTemplate';
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { startCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Container } from '../../layout/Container';
import styled from './components.styled';
import ResourceDownloadForm from './components/form';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advanced);

export interface ResourceDownloadTemplateProps extends ResourceQuery {}

const ResourceDownloadTemplate: PageTemplate<ResourceDownloadTemplateProps> = ({ entry }) => {
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const shouldBypassForm = useBypassCookie(RESOURCE_DOWNLOAD_BYPASS_KEY);

  const webinarLiveTime = useMemo(() => {
    if (entry?.__typename !== 'resources_resourceWebinar_Entry' || !entry.startDateTime) return null;

    const startTime = dayjs(entry.startDateTime);

    /* Do not show if webinar has started or is in the past */
    if (startTime.isBefore(dayjs())) return null;

    const liveDate = startTime.format('MMM DD');
    const liveTime = startTime.format('hh:mm A');
    const timeZone = startTime.format('z');

    return (
      <>
        {liveDate} <span>|</span> {liveTime} {timeZone}
      </>
    );
  }, [entry]);

  const [isWebinar, isWebinarUpcoming, isWebinarReplay, isProductFocused, webinarType] = useMemo(() => {
    let isWebinar = false;
    let isWebinarUpcoming = false;
    let isWebinarReplay = false;
    let isProductFocused = false;
    let webinarType: CraftWebinarType = null;

    if (entry?.__typename == 'resources_resourceWebinar_Entry') {
      isWebinar = true;
      isWebinarUpcoming = webinarLiveTime !== null;
      isWebinarReplay = (!isWebinarUpcoming && !!entry.youtubeVideoUrl) ?? false;
      isProductFocused = entry.isProductFocused ?? false;
      webinarType = entry.webinarType as CraftWebinarType;
    }

    return [isWebinar, isWebinarUpcoming, isWebinarReplay, isProductFocused, webinarType];
  }, [entry, webinarLiveTime]);

  const resourceType: string = useMemo(() => {
    if (entry?.__typename === 'resources_resourceWebinar_Entry') {
      return startCase(entry.webinarType!);
    } else if (entry?.__typename === 'resources_resourceAsset_Entry') {
      return entry.resourceType!;
    } else {
      return 'Resource';
    }
  }, [entry]);

  useEffect(() => {
    if (shouldBypassForm) setFormSubmitted(true);
  }, [shouldBypassForm]);

  useEffect(() => {
    if (entry?.__typename === 'resources_resourceWebinar_Entry' && !entry.requireFormFill && entry.shouldAutoplay) {
      setShowVideoModal(true);
    }
  }, []);

  useEffect(() => {
    setFormSubmitted(false);
  }, [entry?.slug]);

  if (entry?.__typename === 'resources_resourceExternal_Entry') return null;

  const { requireFormFill } = entry!;

  const image = getSingle(entry?.coverImage);
  const parsedResourceContent = JSON.parse(JSON.stringify(entry?.resourceContent?.rawNodes));
  const firstParagraphItem = parsedResourceContent.shift();
  const firstParagraphReEncoded = JSON.stringify(createPlainTextParagraphNode(firstParagraphItem));
  const resourceContentReEncoded = JSON.stringify(parsedResourceContent);

  const onDownloadAsset = (clicked: boolean) => {
    if (clicked) {
      gtm.track('click', {
        element_type: 'button',
        element_label: 'Download',
        location: 'Resource Form',
      });
    }
  };

  const onToggleVideo = (clicked: boolean) => {
    setShowVideoModal(true);

    if (clicked) {
      gtm.track('click', {
        element_type: 'button',
        location: 'Resource Form',
        element_label: 'Watch Video',
      });
    }
  };

  return (
    <>
      <Container>
        <styled.ResourceDownloadPageWrapper>
          <styled.HeaderContainer>
            <styled.HeaderContentWrapper>
              <styled.HeaderTextWrapper>
                <styled.Eyebrow>
                  <strong>{resourceType ?? 'Resource'}</strong>
                  {webinarLiveTime && (
                    <>
                      {' '}
                      <span>|</span> {webinarLiveTime}
                    </>
                  )}
                </styled.Eyebrow>

                <styled.PageHeadline>{entry?.title}</styled.PageHeadline>
                {firstParagraphReEncoded && <VizyBlogArticle rawNodes={firstParagraphReEncoded} nodes={[]} />}
              </styled.HeaderTextWrapper>

              {image && (
                <styled.CoverImageWrapper>
                  <Image src={image.url!} width={image.width!} height={image.height!} alt={image.altText ?? ''} />
                </styled.CoverImageWrapper>
              )}
            </styled.HeaderContentWrapper>
          </styled.HeaderContainer>

          <styled.FormContainer>
            <styled.FormStickyWrapper>
              <ResourceDownloadForm
                {...{ entry, setFormSubmitted, onDownloadAsset, onToggleVideo, formSubmitted }}
                config={{
                  isWebinar,
                  isWebinarUpcoming,
                  isProductFocused,
                  isWebinarReplay,
                  webinarType,
                  requireFormFill: requireFormFill ?? true,
                }}
              />
            </styled.FormStickyWrapper>
          </styled.FormContainer>

          <styled.ContentContainer>
            <styled.MainContentBackgroundContainer>
              <styled.MainContentWrapper>
                {resourceContentReEncoded && <VizyBlogArticle rawNodes={resourceContentReEncoded} nodes={[]} />}

                {entry?.__typename === 'resources_resourceWebinar_Entry' &&
                  (entry.webinarSpeakers ?? []).length > 0 && (
                    <>
                      <styled.WebinarSpeakerHeadline>Speakers</styled.WebinarSpeakerHeadline>

                      <styled.WebinarSpeakerList>
                        {entry.webinarSpeakers?.map((speaker) => {
                          const photo = speaker?.authorPhoto && speaker?.authorPhoto[0];

                          if (!photo) return null;

                          return (
                            <styled.WebinarSpeaker key={speaker?.id}>
                              <styled.WebinarSpeakerImage
                                objectPosition={
                                  photo.focalPoint
                                    ? photo.focalPoint.map((pos) => `${pos! * 100}%`).join(' ')
                                    : 'center'
                                }
                              >
                                <Image src={photo.url!} alt={photo.altText ?? ''} fill />
                              </styled.WebinarSpeakerImage>

                              <h6>
                                <span>{speaker?.authorName}</span>
                                {!!speaker?.authorTitle && (
                                  <>
                                    <span>,&nbsp;</span>
                                    <span>{speaker?.authorTitle}</span>
                                  </>
                                )}
                              </h6>
                            </styled.WebinarSpeaker>
                          );
                        })}
                      </styled.WebinarSpeakerList>
                    </>
                  )}
              </styled.MainContentWrapper>
            </styled.MainContentBackgroundContainer>
          </styled.ContentContainer>
        </styled.ResourceDownloadPageWrapper>

        {showVideoModal && (
          <styled.VideoModal name="Resource Download Video" onClose={() => setShowVideoModal(false)}>
            <styled.VideoModalYouTubeVideo
              videoId={
                entry?.__typename === 'resources_resourceWebinar_Entry'
                  ? extractYouTubeId(entry.youtubeVideoUrl!)!
                  : undefined
              }
              autoplay
              onInitialPlay={() => {
                gtm.track('play_video', {
                  video_title: maybeNotSet(entry?.title),
                  video_url:
                    (entry?.__typename === 'resources_resourceWebinar_Entry' ? entry.youtubeVideoUrl : undefined) ??
                    '(not set)',
                });
              }}
            />
          </styled.VideoModal>
        )}
      </Container>

      {resourceCardsModuleHasCards(entry?.moduleResourceCards) ? (
        <styled.RelatedContainer>
          <Container>
            <ResourceCardsModule props={entry?.moduleResourceCards} />
          </Container>
        </styled.RelatedContainer>
      ) : null}
    </>
  );
};

export default ResourceDownloadTemplate;

ResourceDownloadTemplate.template = MasterTemplate;

const createPlainTextParagraphNode = (node: { content: { text: string }[] }) => {
  const nodeText = node?.content[0]?.text;
  const plainTextNode = {
    ...node,
    type: 'paragraph',
    content: [
      {
        marks: [],
        text: nodeText,
        type: 'text',
      },
    ],
  };

  return [plainTextNode];
};
